import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Building2, Save, Server, Copy, Check, RefreshCw, Stethoscope, ScanBarcode, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { QRSecretCard } from '../components/settings/QRSecretCard';
import { BioTerminalsCard } from '../components/settings/BioTerminalsCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../providers/AuthProvider';
import { getUserDisplayName } from '@tco/shared-types';
import { useTenantSettings, useUpdateTenantSettings, useOrganizationInfo, useTestMarkirovkaConnection, useDamuMedStatus, useTestDamuMedConnection } from '../hooks/settings/useSettings';
import { DamuMedConnectionStatus } from '../components/settings/DamuMedConnectionStatus';
import { useSync1CDirectories } from '../hooks/settings/useSync1C';
import { SyncResultsDialog } from '../components/settings/SyncResultsDialog';
import type { OneCSync_Response } from '../types/onec-sync.types';

// Границы совпадают с валидацией бэкенда (UpdateTenantSettingsDto.onec_timeout_ms)
const ONEC_TIMEOUT_MIN_MS = 5000;
const ONEC_TIMEOUT_MAX_MS = 600000;
const clampOnecTimeout = (ms: number) =>
  Math.min(ONEC_TIMEOUT_MAX_MS, Math.max(ONEC_TIMEOUT_MIN_MS, ms));

export function Settings() {
  const { user } = useAuth();
  const { data: settings, isLoading: isLoadingSettings } = useTenantSettings();
  const { data: organizationInfo, isLoading: isLoadingOrganization } = useOrganizationInfo();
  const updateSettings = useUpdateTenantSettings();

  // 1C Sync hook and state
  const sync1C = useSync1CDirectories();
  const [showSyncResults, setShowSyncResults] = useState(false);
  const [syncResults, setSyncResults] = useState<OneCSync_Response | null>(null);

  // Local state for director name input
  const [directorName, setDirectorName] = useState('');
  const [isDirectorNameDirty, setIsDirectorNameDirty] = useState(false);

  // Local state for medical organization toggle
  const [isMedicalOrganization, setIsMedicalOrganization] = useState(false);

  // Local state for 1C integration fields
  const [onecApiUrl, setOnecApiUrl] = useState('');
  const [onecApiUsername, setOnecApiUsername] = useState('');
  const [onecApiPassword, setOnecApiPassword] = useState('');
  const [onecTimeoutMs, setOnecTimeoutMs] = useState(60000);
  const [is1CSettingsDirty, setIs1CSettingsDirty] = useState(false);

  // Local state for DamuMed integration fields
  const [damumedAuthUrl, setDamumedAuthUrl] = useState('');
  const [damumedApiUrl, setDamumedApiUrl] = useState('');
  const [damumedUsername, setDamumedUsername] = useState('');
  const [damumedPassword, setDamumedPassword] = useState('');
  const [isDamuMedSettingsDirty, setIsDamuMedSettingsDirty] = useState(false);

  // Local state for Markirovka (edo.markirovka.kz) integration fields
  const [markirovkaUsername, setMarkirovkaUsername] = useState('');
  const [markirovkaPassword, setMarkirovkaPassword] = useState('');
  const [markirovkaBin, setMarkirovkaBin] = useState('');
  const [markirovkaCommodityGroup, setMarkirovkaCommodityGroup] = useState('pharma');
  const [isMarkirovkaSettingsDirty, setIsMarkirovkaSettingsDirty] = useState(false);
  const [markirovkaTestResult, setMarkirovkaTestResult] = useState<
    | { ok: true; bin: string; organization_name: string; states_km_count: number }
    | { ok: false; message: string }
    | null
  >(null);
  const testMarkirovkaConnection = useTestMarkirovkaConnection();
  const testDamuMedConnection = useTestDamuMedConnection();
  const [damumedTestError, setDamumedTestError] = useState<string | null>(null);
  const { data: damumedStatus } = useDamuMedStatus(isMedicalOrganization);

  // Local state for tenant ID copy
  const [isTenantIdCopied, setIsTenantIdCopied] = useState(false);

  // Sync director name from settings
  useEffect(() => {
    if (settings?.director_name !== undefined) {
      setDirectorName(settings.director_name || '');
      setIsDirectorNameDirty(false);
    }
  }, [settings?.director_name]);

  // Sync medical organization setting
  useEffect(() => {
    if (settings?.is_medical_organization !== undefined) {
      setIsMedicalOrganization(settings.is_medical_organization);
    }
  }, [settings?.is_medical_organization]);

  // Sync 1C settings from API
  useEffect(() => {
    if (settings) {
      setOnecApiUrl(settings.onec_api_url || '');
      setOnecApiUsername(settings.onec_api_username || '');
      setOnecApiPassword(''); // Never populate password from API
      setOnecTimeoutMs(settings.onec_timeout_ms || 60000);
      setIs1CSettingsDirty(false);
    }
  }, [settings]);

  // Sync DamuMed settings from API
  useEffect(() => {
    if (settings) {
      setDamumedAuthUrl(settings.damumed_auth_url || '');
      setDamumedApiUrl(settings.damumed_api_url || '');
      setDamumedUsername(settings.damumed_username || '');
      setDamumedPassword('');
      setIsDamuMedSettingsDirty(false);
    }
  }, [settings]);

  // Sync Markirovka settings from API
  useEffect(() => {
    if (settings) {
      setMarkirovkaUsername(settings.markirovka_username || '');
      setMarkirovkaBin(settings.markirovka_bin || '');
      setMarkirovkaCommodityGroup(settings.markirovka_commodity_group || 'pharma');
      setMarkirovkaPassword('');
      setIsMarkirovkaSettingsDirty(false);
    }
  }, [settings]);

  const handleDirectorNameSave = async () => {
    try {
      await updateSettings.mutateAsync({
        director_name: directorName || undefined,
      });
      setIsDirectorNameDirty(false);
      console.log('Руководитель организации обновлён');
    } catch (error) {
      console.error('Failed to update director name:', error);
    }
  };

  const handleMedicalOrganizationToggle = async (checked: boolean) => {
    setIsMedicalOrganization(checked);
    try {
      await updateSettings.mutateAsync({
        is_medical_organization: checked,
      });
      console.log('Настройка медицинской организации обновлена');
    } catch (error) {
      console.error('Failed to update medical organization setting:', error);
      // Revert on error
      setIsMedicalOrganization(!checked);
    }
  };

  const handle1CSettingsSave = async () => {
    try {
      await updateSettings.mutateAsync({
        onec_api_url: onecApiUrl || undefined,
        onec_api_username: onecApiUsername || undefined,
        onec_api_password: onecApiPassword || undefined,
        onec_timeout_ms: clampOnecTimeout(onecTimeoutMs),
      });
      setIs1CSettingsDirty(false);
      setOnecApiPassword(''); // Clear password after save
      console.log('Настройки 1С обновлены');
    } catch (error) {
      console.error('Failed to update 1C settings:', error);
    }
  };

  const handleDamuMedSettingsSave = async () => {
    try {
      await updateSettings.mutateAsync({
        damumed_auth_url: damumedAuthUrl || undefined,
        damumed_api_url: damumedApiUrl || undefined,
        damumed_username: damumedUsername || undefined,
        damumed_password: damumedPassword || undefined,
      });
      setIsDamuMedSettingsDirty(false);
      setDamumedPassword('');
      setDamumedTestError(null);
      console.log('Настройки DamuMed обновлены');
    } catch (error) {
      console.error('Failed to update DamuMed settings:', error);
    }
  };

  // Сначала сохраняем изменённые поля (иначе проверялись бы старые данные), затем вход в DamuMed
  const handleDamuMedTest = async (force = false) => {
    setDamumedTestError(null);
    try {
      if (isDamuMedSettingsDirty) {
        await updateSettings.mutateAsync({
          damumed_auth_url: damumedAuthUrl || undefined,
          damumed_api_url: damumedApiUrl || undefined,
          damumed_username: damumedUsername || undefined,
          damumed_password: damumedPassword || undefined,
        });
        setIsDamuMedSettingsDirty(false);
        setDamumedPassword('');
      }
      await testDamuMedConnection.mutateAsync(force);
    } catch (error) {
      setDamumedTestError(
        error instanceof Error ? error.message : 'Не удалось выполнить проверку',
      );
    }
  };

  const handleMarkirovkaSettingsSave = async () => {
    try {
      await updateSettings.mutateAsync({
        markirovka_username: markirovkaUsername || undefined,
        markirovka_password: markirovkaPassword || undefined,
        markirovka_bin: markirovkaBin || undefined,
        markirovka_commodity_group: markirovkaCommodityGroup || undefined,
      });
      setIsMarkirovkaSettingsDirty(false);
      setMarkirovkaPassword('');
      setMarkirovkaTestResult(null);
    } catch (error) {
      console.error('Failed to update Markirovka settings:', error);
    }
  };

  const handleMarkirovkaTest = async () => {
    setMarkirovkaTestResult(null);
    try {
      const result = await testMarkirovkaConnection.mutateAsync({
        username: markirovkaUsername || undefined,
        password: markirovkaPassword || undefined,
        bin: markirovkaBin || undefined,
        commodity_group: markirovkaCommodityGroup || undefined,
      });
      setMarkirovkaTestResult({
        ok: true,
        bin: result.bin,
        organization_name: result.organization_name,
        states_km_count: result.states_km_count,
      });
      setMarkirovkaPassword('');
      setIsMarkirovkaSettingsDirty(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Не удалось подключиться';
      setMarkirovkaTestResult({ ok: false, message });
    }
  };

  const handleCopyTenantId = async () => {
    if (organizationInfo?.id) {
      try {
        await navigator.clipboard.writeText(organizationInfo.id);
        setIsTenantIdCopied(true);
        setTimeout(() => setIsTenantIdCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy tenant ID:', error);
      }
    }
  };

  const handleSync1CClick = async () => {
    try {
      setSyncResults(null);
      setShowSyncResults(true);
      const result = await sync1C.mutateAsync();
      setSyncResults(result);
    } catch (error) {
      console.error('Sync failed:', error);
      // Results dialog will show error state
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs />

      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          Настройки
        </h1>
        <p className="text-muted-foreground mt-2">
          Управление настройками организации и системы
        </p>
      </div>

      {/* Organization info card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Информация об организации
          </CardTitle>
          <CardDescription>
            Основные данные вашей организации
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Organization Basic Info (Read-only) */}
          <div className="grid grid-cols-2 gap-4">
            {/* Tenant ID */}
            <div className="col-span-2">
              <p className="text-sm font-medium text-muted-foreground">Tenant ID (Идентификатор организации)</p>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1">
                  {isLoadingOrganization ? '...' : organizationInfo?.id || '—'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyTenantId}
                  disabled={!organizationInfo?.id}
                  className="h-7 px-2"
                >
                  {isTenantIdCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-muted-foreground">Наименование организации</p>
              <p className="text-sm">{isLoadingOrganization ? '...' : organizationInfo?.name || '—'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">БИН/ИНН</p>
              <p className="text-sm">{isLoadingOrganization ? '...' : organizationInfo?.inn || '—'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Администратор</p>
              <p className="text-sm">{user ? getUserDisplayName(user) : '—'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-sm">{user?.email || '—'}</p>
            </div>
          </div>

          {/* Director Name (Editable) */}
          <div className="pt-3 border-t">
            <Label htmlFor="director-name" className="text-sm font-medium">
              Руководитель организации
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              ФИО генерального директора, CEO или руководителя
            </p>
            <div className="flex gap-2">
              <Input
                id="director-name"
                value={directorName}
                onChange={(e) => {
                  setDirectorName(e.target.value);
                  setIsDirectorNameDirty(true);
                }}
                placeholder="Иванов Иван Иванович"
                disabled={isLoadingSettings || updateSettings.isPending}
                className="flex-1"
              />
              <Button
                onClick={handleDirectorNameSave}
                disabled={!isDirectorNameDirty || updateSettings.isPending}
                size="default"
              >
                <Save className="h-4 w-4 mr-2" />
                Сохранить
              </Button>
            </div>
          </div>

          {/* Medical Organization Toggle */}
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is-medical-organization" className="text-sm font-medium">
                  Это медицинская организация
                </Label>
                <p className="text-xs text-muted-foreground">
                  Включите, если ваша организация относится к медицинским учреждениям
                </p>
              </div>
              <Switch
                id="is-medical-organization"
                checked={isMedicalOrganization}
                onCheckedChange={handleMedicalOrganizationToggle}
                disabled={isLoadingSettings || updateSettings.isPending}
              />
            </div>
          </div>

        </CardContent>
      </Card>

      {/* QR Secret Management */}
      <QRSecretCard />

      {/* Биометрические терминалы: агенты объектов и устройства */}
      <BioTerminalsCard />

      {/* 1C Integration Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Интеграция с 1С
          </CardTitle>
          <CardDescription>
            Настройки подключения к вашей системе 1С для синхронизации данных
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 1C API URL */}
          <div>
            <Label htmlFor="onec-api-url" className="text-sm font-medium">
              URL 1С API
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Адрес вашего сервера 1С (например: http://1c-server.company.local:8080/api)
            </p>
            <Input
              id="onec-api-url"
              type="url"
              value={onecApiUrl}
              onChange={(e) => {
                setOnecApiUrl(e.target.value);
                setIs1CSettingsDirty(true);
              }}
              placeholder="http://1c-server.company.local:8080/api"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* 1C API Username */}
          <div>
            <Label htmlFor="onec-api-username" className="text-sm font-medium">
              Имя пользователя
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Логин для доступа к 1С API
            </p>
            <Input
              id="onec-api-username"
              type="text"
              value={onecApiUsername}
              onChange={(e) => {
                setOnecApiUsername(e.target.value);
                setIs1CSettingsDirty(true);
              }}
              placeholder="tco_integration"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* 1C API Password */}
          <div>
            <Label htmlFor="onec-api-password" className="text-sm font-medium">
              Пароль
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Пароль будет зашифрован при сохранении. Оставьте пустым, чтобы не изменять.
            </p>
            <Input
              id="onec-api-password"
              type="password"
              value={onecApiPassword}
              onChange={(e) => {
                setOnecApiPassword(e.target.value);
                setIs1CSettingsDirty(true);
              }}
              placeholder="••••••••"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* 1C API Timeout */}
          <div>
            <Label htmlFor="onec-timeout" className="text-sm font-medium">
              Таймаут (мс)
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Максимальное время ожидания ответа от 1С в миллисекундах ({ONEC_TIMEOUT_MIN_MS}-{ONEC_TIMEOUT_MAX_MS}, т.е. до 10 минут)
            </p>
            <Input
              id="onec-timeout"
              type="number"
              min={ONEC_TIMEOUT_MIN_MS}
              max={ONEC_TIMEOUT_MAX_MS}
              step={1000}
              value={onecTimeoutMs}
              onChange={(e) => {
                setOnecTimeoutMs(parseInt(e.target.value) || 60000);
                setIs1CSettingsDirty(true);
              }}
              onBlur={() => setOnecTimeoutMs(clampOnecTimeout(onecTimeoutMs))}
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* Save Button */}
          <div className="pt-2">
            <Button
              onClick={handle1CSettingsSave}
              disabled={!is1CSettingsDirty || updateSettings.isPending}
              size="default"
              className="w-full sm:w-auto"
            >
              <Save className="h-4 w-4 mr-2" />
              Сохранить настройки 1С
            </Button>
          </div>

          {/* Sync Button */}
          <div className="pt-2 border-t">
            <Button
              onClick={handleSync1CClick}
              disabled={!onecApiUrl || sync1C.isPending}
              size="default"
              variant="outline"
              className="w-full sm:w-auto"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${sync1C.isPending ? 'animate-spin' : ''}`} />
              Синхронизировать справочники
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Запустить синхронизацию подразделений, должностей, графиков и сотрудников из 1С
            </p>
          </div>
        </CardContent>
      </Card>

      {/* DamuMed Integration Settings (only for medical organizations) */}
      {isMedicalOrganization && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5" />
              Интеграция с DamuMed
            </CardTitle>
            <CardDescription>
              Настройки подключения к системе DamuMed для получения назначений пациентов
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* DamuMed Auth URL */}
            <div>
              <Label htmlFor="damumed-auth-url" className="text-sm font-medium">
                URL авторизации
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Базовый URL сервера авторизации DamuMed (без /api/..., например: https://tur.dmed.kz)
              </p>
              <Input
                id="damumed-auth-url"
                type="url"
                value={damumedAuthUrl}
                onChange={(e) => {
                  setDamumedAuthUrl(e.target.value);
                  setIsDamuMedSettingsDirty(true);
                }}
                placeholder="https://tur.dmed.kz"
                disabled={isLoadingSettings || updateSettings.isPending}
              />
            </div>

            {/* DamuMed API URL */}
            <div>
              <Label htmlFor="damumed-api-url" className="text-sm font-medium">
                URL API
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Базовый URL API DamuMed (без /api_new/..., например: https://hospital-tur.dmed.kz)
              </p>
              <Input
                id="damumed-api-url"
                type="url"
                value={damumedApiUrl}
                onChange={(e) => {
                  setDamumedApiUrl(e.target.value);
                  setIsDamuMedSettingsDirty(true);
                }}
                placeholder="https://hospital-tur.dmed.kz"
                disabled={isLoadingSettings || updateSettings.isPending}
              />
            </div>

            {/* DamuMed Username */}
            <div>
              <Label htmlFor="damumed-username" className="text-sm font-medium">
                Имя пользователя
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Логин для доступа к DamuMed API
              </p>
              <Input
                id="damumed-username"
                type="text"
                value={damumedUsername}
                onChange={(e) => {
                  setDamumedUsername(e.target.value);
                  setIsDamuMedSettingsDirty(true);
                }}
                placeholder="tur_sauran_330"
                disabled={isLoadingSettings || updateSettings.isPending}
              />
            </div>

            {/* DamuMed Password */}
            <div>
              <Label htmlFor="damumed-password" className="text-sm font-medium">
                Пароль
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Пароль будет зашифрован при сохранении. Оставьте пустым, чтобы не изменять.
              </p>
              <Input
                id="damumed-password"
                type="password"
                value={damumedPassword}
                onChange={(e) => {
                  setDamumedPassword(e.target.value);
                  setIsDamuMedSettingsDirty(true);
                }}
                placeholder="••••••••"
                disabled={isLoadingSettings || updateSettings.isPending}
              />
            </div>

            {/* Connection status / test result */}
            {damumedTestError ? (
              <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950 dark:text-red-100 dark:border-red-900">
                <div className="font-medium">Проверка не выполнена</div>
                <div className="mt-1 text-xs">{damumedTestError}</div>
              </div>
            ) : (
              damumedStatus && (
                <DamuMedConnectionStatus
                  status={damumedStatus}
                  onForceTest={() => handleDamuMedTest(true)}
                  isTesting={testDamuMedConnection.isPending}
                />
              )
            )}

            {/* Buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={handleDamuMedSettingsSave}
                disabled={!isDamuMedSettingsDirty || updateSettings.isPending}
                size="default"
              >
                <Save className="h-4 w-4 mr-2" />
                Сохранить настройки DamuMed
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDamuMedTest(false)}
                disabled={testDamuMedConnection.isPending || updateSettings.isPending}
                size="default"
                title={isDamuMedSettingsDirty ? 'Изменения будут сохранены перед проверкой' : undefined}
              >
                {testDamuMedConnection.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {isDamuMedSettingsDirty ? 'Сохранить и проверить' : 'Проверить подключение'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Markirovka (edo.markirovka.kz) Integration Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScanBarcode className="h-5 w-5" />
            Интеграция с «Маркировка» (edo.markirovka.kz)
          </CardTitle>
          <CardDescription>
            Учётная запись организации в системе маркировки для сканирования коробок маркированных лекарств в мобильном приложении
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status indicator */}
          <div className="flex items-center gap-2 text-sm">
            {settings?.markirovka_is_active ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-green-700 dark:text-green-400">
                  Активна
                </span>
                {settings?.markirovka_refresh_valid_until && (
                  <span className="text-muted-foreground">
                    · сессия до {new Date(settings.markirovka_refresh_valid_until).toLocaleString('ru-RU')}
                  </span>
                )}
              </>
            ) : settings?.markirovka_username ? (
              <>
                <XCircle className="h-4 w-4 text-amber-600" />
                <span className="text-amber-700 dark:text-amber-400">
                  Требуется повторный вход
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">Не настроено</span>
            )}
          </div>

          {/* Username */}
          <div>
            <Label htmlFor="markirovka-username" className="text-sm font-medium">
              Логин
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Email учётной записи в edo.markirovka.kz (Keycloak)
            </p>
            <Input
              id="markirovka-username"
              type="text"
              value={markirovkaUsername}
              onChange={(e) => {
                setMarkirovkaUsername(e.target.value);
                setIsMarkirovkaSettingsDirty(true);
              }}
              placeholder="kurm-srb@yandex.kz"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* Password */}
          <div>
            <Label htmlFor="markirovka-password" className="text-sm font-medium">
              Пароль
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Будет зашифрован при сохранении. Оставьте пустым, чтобы не изменять.
            </p>
            <Input
              id="markirovka-password"
              type="password"
              value={markirovkaPassword}
              onChange={(e) => {
                setMarkirovkaPassword(e.target.value);
                setIsMarkirovkaSettingsDirty(true);
              }}
              placeholder="••••••••"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* BIN */}
          <div>
            <Label htmlFor="markirovka-bin" className="text-sm font-medium">
              БИН организации
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              12 цифр. Можно оставить пустым — подтянется автоматически при «Проверить подключение».
            </p>
            <Input
              id="markirovka-bin"
              type="text"
              inputMode="numeric"
              maxLength={12}
              value={markirovkaBin}
              onChange={(e) => {
                setMarkirovkaBin(e.target.value.replace(/\D/g, '').slice(0, 12));
                setIsMarkirovkaSettingsDirty(true);
              }}
              placeholder="070240006206"
              disabled={isLoadingSettings || updateSettings.isPending}
            />
          </div>

          {/* Commodity group */}
          <div>
            <Label htmlFor="markirovka-commodity-group" className="text-sm font-medium">
              Товарная группа
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              pharma — лекарства, shoes — обувь
            </p>
            <select
              id="markirovka-commodity-group"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={markirovkaCommodityGroup}
              onChange={(e) => {
                setMarkirovkaCommodityGroup(e.target.value);
                setIsMarkirovkaSettingsDirty(true);
              }}
              disabled={isLoadingSettings || updateSettings.isPending}
            >
              <option value="pharma">pharma — лекарства</option>
              <option value="shoes">shoes — обувь</option>
            </select>
          </div>

          {/* Test result */}
          {markirovkaTestResult && (
            <div
              className={`rounded-md border p-3 text-sm ${
                markirovkaTestResult.ok
                  ? 'border-green-200 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100 dark:border-green-900'
                  : 'border-red-200 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100 dark:border-red-900'
              }`}
            >
              {markirovkaTestResult.ok ? (
                <>
                  <div className="font-medium">Подключение успешно</div>
                  <div className="mt-1 text-xs">
                    БИН: {markirovkaTestResult.bin} · {markirovkaTestResult.organization_name}
                  </div>
                  <div className="mt-0.5 text-xs">
                    Справочник статусов КМ: {markirovkaTestResult.states_km_count} записей
                  </div>
                </>
              ) : (
                <>
                  <div className="font-medium">Не удалось подключиться</div>
                  <div className="mt-1 text-xs">{markirovkaTestResult.message}</div>
                </>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleMarkirovkaSettingsSave}
              disabled={!isMarkirovkaSettingsDirty || updateSettings.isPending}
              size="default"
            >
              <Save className="h-4 w-4 mr-2" />
              Сохранить
            </Button>
            <Button
              variant="outline"
              onClick={handleMarkirovkaTest}
              disabled={testMarkirovkaConnection.isPending}
              size="default"
            >
              {testMarkirovkaConnection.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Проверить подключение
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync Results Dialog */}
      <SyncResultsDialog
        open={showSyncResults}
        onOpenChange={setShowSyncResults}
        syncResult={syncResults}
        isLoading={sync1C.isPending}
      />
    </div>
  );
}
